/**
 * CSV read/write for the bulk "Completed Order" flow, plus the sample
 * template generator.
 *
 * Rows here hold NAMES throughout — exactly what a person typed or picked in
 * the Google Sheet. Nothing is resolved to an id on this side any more:
 * POST /v1/historical-orders/validate resolves every reference against live
 * master data, reports which row and column is wrong, and hands back the
 * resolved ids the submit step then uses. That keeps one authority for
 * "is this combination real", instead of a client-side guess that can
 * disagree with the server.
 */

import { CSV_COLUMNS, FIELD_DEFS, FIELD_TYPE } from "./fieldDefs";

export { CSV_COLUMNS };

/**
 * The two columns naming the report and metadata file each row expects. They
 * were previously folded into the file columns' own labels and then dropped
 * on the floor — the value someone had already typed in the sheet was parsed
 * and discarded. Kept now, because it's the one unambiguous way to say which
 * file on disk belongs to which row, and matchFilesToRows reads it first.
 *
 * Deliberately NOT part of CSV_COLUMNS: that list is the required-header
 * check, and a sheet exported without these columns must still import (it
 * just falls back to matching on the row's identifiers instead).
 */
export const FILE_NAME_COLUMNS = [
  ["Report File Name", "reportFileName", ["report file name", "report file"]],
  ["Report Metadata File Name", "reportMetadataFileName", ["report metadata file name", "report metadata file"]],
];

export const TARGET_END_STATUS = Object.freeze({
  REPORT_UPLOADED: "Report Uploaded",
  COMPLETED: "Completed",
});

export const PARSE_DELAY_MS = 400;

// Enough to cover every company in a normal dataset while keeping the
// template small enough to read through by hand.
const MAX_SAMPLE_COMPANIES = 8;

let rowSeq = 0;
const nextRowId = () => `row-${++rowSeq}`;

function csvField(value) {
  const str = value === null || value === undefined ? "" : String(value);
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

export function rowsToCsvString(dataRows) {
  const dateLabels = new Set(FIELD_DEFS.filter((def) => def.type === FIELD_TYPE.DATE).map((def) => def.label));
  const header = CSV_COLUMNS.map(([label]) => csvField(dateLabels.has(label)
    ? `${label} [YYYY-MM-DD]` : label));
  const lines = [header.join(",")];
  dataRows.forEach((row) => {
    lines.push(CSV_COLUMNS.map(([, key]) => csvField(row[key])).join(","));
  });
  return lines.join("\r\n");
}

function parseCsvTable(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  const endField = () => { row.push(field); field = ""; };
  const endRow = () => { endField(); rows.push(row); row = []; };
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (char === '"') inQuotes = false;
      else field += char;
    } else if (char === '"') inQuotes = true;
    else if (char === ",") endField();
    else if (char === "\n") endRow();
    else if (char !== "\r") field += char;
  }
  if (field !== "" || row.length > 0) endRow();
  return rows.filter((cells) => cells.some((c) => c.trim() !== ""));
}

// A template header carries a date-format hint and/or a Required/Optional
// marker, and nothing guarantees which order they trail in — "Completed Date
// [YYYY-MM-DD] (Required when Target End Status is Completed)" is both at
// once. Stripping them in a fixed sequence only worked for one arrangement:
// with the date hint taken off first, that header kept its bracket once the
// marker came off, matched no known column, and failed the import with
// "Missing column(s): Completed Date". Strip whatever trails, repeatedly,
// until nothing more does.
const HEADER_SUFFIX = /\s*(?:\[YYYY-MM-DD(?: HH:mm; 24-hour)?\]|\((?:Required(?: when Target End Status is Completed)?|Optional)\))\s*$/i;

function stripHeaderSuffixes(label) {
  let out = String(label === null || label === undefined ? "" : label).trim();
  let previous;
  do {
    previous = out;
    out = out.replace(HEADER_SUFFIX, "").trim();
  } while (out !== previous);
  return out;
}

/**
 * Parses a real sheet export into row objects. Column order doesn't matter —
 * matching is by header label, case-insensitive — but every column must be
 * present, since a missing one would silently import blank values.
 */
export function csvStringToRows(text) {
  const table = parseCsvTable(text);
  if (!table.length) throw new Error("The file is empty.");

  const header = table[0].map((h) => stripHeaderSuffixes(h).toLowerCase());
  const missing = CSV_COLUMNS.filter(([label]) => !header.includes(label.toLowerCase()));
  if (missing.length) {
    throw new Error(`Missing column(s): ${missing.map(([label]) => label).join(", ")}`);
  }

  rowSeq = 0;
  return table.slice(1).map((cells) => {
    const row = { rowId: nextRowId() };
    // File columns (reportFile/reportMetadataFile, every optional sample
    // photo) never come from the CSV — they're attached in the grid — so
    // every one starts null here regardless of what CSV_COLUMNS covers.
    FIELD_DEFS.filter((def) => def.type === FIELD_TYPE.FILE).forEach((def) => { row[def.field] = null; });
    CSV_COLUMNS.forEach(([label, key]) => {
      row[key] = (cells[header.indexOf(label.toLowerCase())] || "").trim();
    });
    // Optional, so a missing column is blank rather than an import failure.
    FILE_NAME_COLUMNS.forEach(([, key, aliases]) => {
      const index = aliases.map((alias) => header.indexOf(alias)).find((i) => i >= 0);
      row[key] = index === undefined ? "" : (cells[index] || "").trim();
    });
    return row;
  });
}

/**
 * The report and metadata files are real File objects the reviewer attaches
 * here — they haven't been uploaded at validation time, so the server
 * genuinely can't see them. This is the one check that has to live on this
 * side; it returns the same { field, label, message } shape the server uses
 * so both merge into a single error list.
 */
export function fileErrorsForRow(row) {
  const errors = [];
  if (!row.reportFile) {
    errors.push({ field: "reportFile", label: "Report File", message: "must be uploaded" });
  } else if (row.reportFile.size === 0) {
    errors.push({ field: "reportFile", label: "Report File", message: "file is empty (0 bytes)" });
  }
  // Required here even though the create service will accept a row without it
  // — a completed order is expected to carry both documents, and the grid is
  // the only place that can tell whether a file was actually attached.
  if (!row.reportMetadataFile) {
    errors.push({ field: "reportMetadataFile", label: "Report Metadata File", message: "must be uploaded" });
  } else if (row.reportMetadataFile.size === 0) {
    errors.push({ field: "reportMetadataFile", label: "Report Metadata File", message: "file is empty (0 bytes)" });
  }
  return errors;
}

function daysAgo(days) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
}

function blankRow() {
  const row = { rowId: nextRowId() };
  // File columns hold a real File object (or null), never a string — every
  // other column starts blank. Covers reportFile/reportMetadataFile and every
  // optional sample-photo column the same way, rather than naming each one.
  FIELD_DEFS.forEach((def) => {
    row[def.field] = def.type === FIELD_TYPE.FILE ? null : "";
  });
  FILE_NAME_COLUMNS.forEach(([, key]) => { row[key] = ""; });
  return row;
}

// Every stage date in order, spread backwards from today so the chain is
// chronologically valid without needing to be hand-tuned.
function withDates(row, overrides = {}) {
  return {
    ...row,
    dateOfPurchase: daysAgo(70),
    pendingDate: daysAgo(65),
    bookedDate: daysAgo(60),
    inReviewDate: daysAgo(55),
    readyForPickupDate: daysAgo(50),
    outForPickupDate: daysAgo(45),
    deliveredToLabDate: daysAgo(40),
    receivedAtLabDate: daysAgo(35),
    underTestingDate: daysAgo(30),
    expectedDateOfCompletion: daysAgo(20),
    reportUploadedDate: daysAgo(15),
    targetEndStatus: TARGET_END_STATUS.REPORT_UPLOADED,
    completedDate: "",
    ...overrides,
  };
}

/**
 * Walks the real master data for complete, genuinely valid
 * Company -> Project -> Commodity -> Variant -> Package chains, one per
 * company, derived from actual Package records rather than assumed.
 */
function realChains(masterData) {
  const companyById = new Map((masterData.companies || []).map((c) => [c.id, c]));
  const projectById = new Map((masterData.projects || []).map((p) => [p.id, p]));
  const commodityPairs = masterData.commodities || [];
  const seen = new Set();
  const chains = [];

  (masterData.packages || []).forEach((pkg) => {
    const company = companyById.get(pkg.company_id);
    if (!company || !pkg.name || !pkg.variant || seen.has(company.id)) return;

    // commodities is a project<->commodity PAIR list, so one commodity id can
    // appear under several projects — including projects owned by other
    // companies. Pick the pair whose project belongs to this package's own
    // company, otherwise the chain silently crosses company boundaries.
    const pair = commodityPairs.find((c) => {
      if (c.id !== pkg.commodity_id) return false;
      const project = projectById.get(c.project_id);
      return !!project && project.company_id === company.id;
    });
    const project = pair ? projectById.get(pair.project_id) : null;
    if (!pair || !project) return;

    seen.add(company.id);
    chains.push({
      company: company.name,
      projectCode: project.code,
      commodity: pair.name,
      sampleVariant: pkg.variant,
      package: pkg.name,
    });
  });

  return chains;
}

function firstStateWithDistricts(masterData) {
  const states = masterData.states || [];
  const districts = masterData.districts || [];
  for (const state of states) {
    const own = districts.filter((d) => d.state_id === state.id);
    if (own.length) return { state, districts: own };
  }
  return { state: states[0] || null, districts: [] };
}

function baseRow(masterData, chain, index) {
  const { state, districts } = firstStateWithDistricts(masterData);
  const agency = (masterData.agencies || [])[0];
  const lab = (masterData.labs || [])[0];
  const logistics = (masterData.logistics || [])[0];

  return withDates({
    ...blankRow(),
    ...chain,
    sampleType: "Whole",
    sector: "Organized",
    state: state ? state.name : "",
    district: districts[0] ? districts[0].name : "",
    sampler: "",
    siteAddress: "Sample site address",
    batchId: `BATCH-${String(index + 1).padStart(3, "0")}`,
    supplier: `${chain.company} Supplier`,
    quantity: "10",
    quantityUnit: "Kg",
    notes: "Sample row generated from live reference data",
    nameOfShop: "Sample Shop",
    addressOfShop: "Sample Shop Address",
    sampleWeight: "500",
    sampleCost: "400",
    typeOfShop: "Retail",
    sampleSealNo: "",
    qrCodeId: "",
    samplePackedIn: "General Packing",
    fssaiLogoPresent: "Yes",
    vegSymbolPresent: "Yes",
    assignedLab: lab ? lab.name : "",
    assignedLogistics: logistics ? logistics.name : "",
    trackingId: "",
    samplePhysicalCondition: "Fit for analysis",
    conditionOfSeal: "Intact",
    sampleRegistrationNumber: "",
    overallSafetyCompliant: "Safe",
    overallQualityCompliant: "Standard",
    overallLabellingCompliant: "Branded",
    overallSampleClassification: "Compliant",
    agency: agency ? agency.name : "",
  });
}

/**
 * The prefilled template: one genuinely valid row per company that has a
 * complete chain in the live data, followed by deliberately broken rows
 * built from those same real values — a package borrowed from another
 * company, a district from another state, a bad option, a date out of order,
 * a missing required field. They're there so the review grid's error
 * marking, the error list and Re-validate can be exercised on a real import
 * without anyone hand-breaking a file first.
 */
export function generateSampleRows(masterData) {
  rowSeq = 0;
  // One valid row per company that has a complete chain, rather than a fixed
  // few — which company sorts first depends on package order, so capping low
  // would silently drop whichever companies happened to land last.
  const chains = realChains(masterData).slice(0, MAX_SAMPLE_COMPANIES);
  if (!chains.length) return [];

  const valid = chains.map((chain, index) => baseRow(masterData, chain, index));
  const broken = [];

  // A package that really exists, but belongs to a different company.
  if (chains.length > 1) {
    broken.push({
      ...baseRow(masterData, chains[0], valid.length),
      package: chains[1].package,
      batchId: "BATCH-BAD-PACKAGE",
    });
  }

  // A project code that really exists, under a different company — plus a
  // district that belongs to a different state.
  if (chains.length > 1) {
    const { state } = firstStateWithDistricts(masterData);
    const otherDistrict = (masterData.districts || []).find((d) => !state || d.state_id !== state.id);
    broken.push({
      ...baseRow(masterData, chains[0], valid.length + 1),
      projectCode: chains[1].projectCode,
      district: otherDistrict ? otherDistrict.name : "Nowhere",
      batchId: "BATCH-BAD-SCOPE",
    });
  }

  // A blank required field, an option that isn't in the list, and a stage
  // date that predates the one before it.
  broken.push({
    ...baseRow(masterData, chains[0], valid.length + 2),
    supplier: "",
    sampleType: "Gaseous",
    quantity: "not-a-number",
    pendingDate: daysAgo(80),
    batchId: "BATCH-BAD-FIELDS",
  });

  return valid.concat(broken);
}
